import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import type { Toast } from 'react-hot-toast';
import toast from 'react-hot-toast';

import { NotificationBanner } from './NotificationBanner';

export interface PromiseNotificationBannerProps {
  type: 'promise';
  promise: Promise<unknown>;
  successMessage: ReactNode;
  /**
   * Either a fixed node, or a resolver called with the rejection reason — see
   * the note on `NotificationDetail['errorMessage']` (COI-900).
   */
  errorMessage: ReactNode | ((error: unknown) => ReactNode);
  loadingMessage?: ReactNode;
  /** Reject values matching this dismiss the toast silently (no error shown). */
  suppressOn?: (error: unknown) => boolean;
  toast: Toast;
}

export const PromiseNotificationBanner = (
  props: PromiseNotificationBannerProps
) => {
  const { promise, suppressOn, successMessage, errorMessage, loadingMessage } =
    props;
  const toastId = props.toast.id;
  const [state, setState] = useState<'error' | 'loading' | 'success'>(
    'loading'
  );
  // Held so an `errorMessage` resolver can render a message derived from WHY
  // the mutation failed. Boxed in an object rather than stored bare: a reject
  // value can itself be a function, which `useState` would call as a lazy
  // initialiser.
  const [rejection, setRejection] = useState<{ reason: unknown }>({
    reason: undefined,
  });

  useEffect(() => {
    setState('loading');
    promise
      .then(() => {
        setState('success');
      })
      .catch((error: unknown) => {
        // A "no-op" outcome (e.g. user-cancelled change-request confirm) should
        // produce no toast at all — dismiss silently rather than show an error.
        if (suppressOn?.(error)) {
          toast.dismiss(toastId);
        } else {
          setRejection({ reason: error });
          setState('error');
        }
      });
  }, [promise, suppressOn, toastId]);

  const content = useMemo(() => {
    switch (state) {
      case 'loading':
        return loadingMessage ?? 'Saving changes...';
      case 'success':
        return successMessage;
      case 'error':
        return typeof errorMessage === 'function'
          ? errorMessage(rejection.reason)
          : errorMessage;
    }
  }, [state, errorMessage, successMessage, loadingMessage, rejection]);

  return (
    <NotificationBanner
      toast={props.toast}
      type={state}
      content={content}
      durationMs={state === 'success' ? 3500 : Infinity}
      // Dismissable in any terminal state (success as well as error), not just
      // error — parity with the plain success/error toasts. This lets the E2E
      // notification helper clear the toast with a click instead of blocking on
      // the ~3.5s success auto-dismiss, whose cumulative cost tipped heavy
      // multi-step specs past the per-test timeout on the slower V3 lane
      // (RSP-6569). Loading stays non-dismissable — it's mid-flight.
      dismissable={state !== 'loading'}
    />
  );
};
